import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const PHONE = "0604026840";
const PHONE_DISPLAY = "06 04 02 68 40";
const WHATSAPP_URL = `https://wa.me/33604026840?text=Bonjour%2C%20j%27ai%20besoin%20d%27un%20serrurier%20en%20urgence`;
const TIKTOK_URL = "https://www.tiktok.com/@securycles.serrur?_t=8k12IzZz8Fs&_r=1";
const GMB_URL = "https://maps.app.goo.gl/kadTYV8afmpfdVSu8";

const navLinks = [
  { label: "Accueil", href: "/#accueil" },
  { label: "Services", href: "/#services" },
  { label: "Zones d'intervention", href: "/#zones" },
  { label: "Avis clients", href: "/#avis" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isLegal = location.pathname === "/mentions-legales";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setMenuOpen(false);
    if (href.startsWith("/#")) {
      const id = href.replace("/#", "");
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          scrolled || isLegal
            ? "bg-white shadow-sm border-b border-gray-100"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <img
              src="https://static.readdy.ai/image/8892c16dc9d95083db2eef95f931ffc6/474014a289bcb6e7215bd7741beb01c1.png"
              alt="Securycles - Serrurier Créteil"
              className="h-10 md:h-12 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => {
                  if (link.href.startsWith("/#")) {
                    e.preventDefault();
                    handleNavClick(link.href);
                  }
                }}
                className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  scrolled || isLegal
                    ? "text-gray-700 hover:text-[#FFB100]"
                    : "text-white hover:text-[#FFB100]"
                }`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-2">
            {/* TikTok */}
            <a
              href={TIKTOK_URL}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                scrolled || isLegal
                  ? "text-gray-600 hover:text-black"
                  : "text-white hover:text-[#FFB100]"
              }`}
              aria-label="TikTok Securycles"
            >
              <i className="ri-tiktok-line text-lg"></i>
            </a>
            {/* GMB */}
            <a
              href={GMB_URL}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                scrolled || isLegal
                  ? "text-gray-600 hover:text-[#FFB100]"
                  : "text-white hover:text-[#FFB100]"
              }`}
              aria-label="Google My Business Securycles"
            >
              <i className="ri-map-pin-line text-lg"></i>
            </a>
            {/* WhatsApp */}
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="flex items-center gap-2 bg-[#FFB100] text-black text-sm font-bold px-4 py-2 rounded-full hover:bg-yellow-400 transition-colors cursor-pointer whitespace-nowrap"
              aria-label="WhatsApp Securycles"
            >
              <i className="ri-whatsapp-line text-base"></i>
              WhatsApp
            </a>
            {/* Phone */}
            <a
              href={`tel:${PHONE}`}
              className="flex items-center gap-2 border-2 border-[#FFB100] text-[#FFB100] text-sm font-bold px-4 py-2 rounded-full hover:bg-[#FFB100] hover:text-black transition-colors cursor-pointer whitespace-nowrap"
              aria-label={`Appeler Securycles au ${PHONE_DISPLAY}`}
            >
              <i className="ri-phone-line text-base"></i>
              {PHONE_DISPLAY}
            </a>
          </div>

          {/* Mobile: phone + burger */}
          <div className="flex lg:hidden items-center gap-2">
            <a
              href={`tel:${PHONE}`}
              className="flex items-center gap-1 bg-[#FFB100] text-black text-xs font-bold px-3 py-2 rounded-full whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line"></i>
              Appeler
            </a>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className={`w-9 h-9 flex items-center justify-center rounded-full cursor-pointer ${
                scrolled || isLegal ? "text-black" : "text-white"
              }`}
              aria-label="Menu"
            >
              <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 px-4 py-4 flex flex-col gap-3">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => {
                  if (link.href.startsWith("/#")) {
                    e.preventDefault();
                    handleNavClick(link.href);
                  }
                }}
                className="text-gray-800 font-medium py-2 border-b border-gray-50 cursor-pointer"
              >
                {link.label}
              </a>
            ))}
            <div className="flex gap-2 pt-2">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 bg-[#FFB100] text-black font-bold py-2 rounded-full text-sm cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line"></i> WhatsApp
              </a>
              <a
                href={TIKTOK_URL}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center border border-gray-200 rounded-full text-gray-700 cursor-pointer"
              >
                <i className="ri-tiktok-line"></i>
              </a>
              <a
                href={GMB_URL}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center border border-gray-200 rounded-full text-gray-700 cursor-pointer"
              >
                <i className="ri-map-pin-line"></i>
              </a>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
